import React, { useState, useEffect, useRef, useMemo } from "react";
import FormFactory from "../../../../components/form-factory";
import { GroupProp } from "../../../../components/form-factory/utils/util";
import { FilterType } from "./type";
import * as S from "./styles";
import { makeRemoteAddresss } from "../../../../../main/factories/usecases/address-controller/remote-address-controller-factory";
import { makeRemoteNationalService } from "../../../../../main/factories/usecases/national-service/remote-national-service-factory";
import { CityProps, StateProps } from "../../../../../domain/models/address.model";
import { NationalServiceModel } from "../../../../../domain/models/national-service";
import { formatNationalTaxCode } from "../../../../../utils/tax";

type FiltersProps = {
  onChange?: (filters: FilterType) => void;
};

export const Filters = ({ onChange }: FiltersProps): JSX.Element => {
  const firstRenderRef = useRef<boolean>(true);

  const [filters, setFilters] = useState<FilterType>({ page: 0, size: 10 });
  const [states, setStates] = useState<StateProps[]>([]);
  const [cities, setCities] = useState<CityProps[]>([]);
  const [nationalServices, setNationalServices] = useState<NationalServiceModel[]>([]);
  const [selectedState, setSelectedState] = useState<number>();

  const addressService = useMemo(() => makeRemoteAddresss(), []);
  const nsService = useMemo(() => makeRemoteNationalService(), []);

  // Load States
  useEffect(() => {
    (async () => {
      try {
        const response = await addressService.loadStates();
        setStates(response);
      } catch (error) {
        console.error("Failed to load states", error);
      }
    })();
  }, [addressService]);

  // Load National Services on mount
  useEffect(() => {
    (async () => {
      try {
        const response = await nsService.search("");
        setNationalServices(response);
      } catch (error) {
        console.error("Failed to load national services", error);
      }
    })();
  }, [nsService]);

  // Load Cities when state changes
  useEffect(() => {
    if (selectedState) {
      (async () => {
        try {
          const response = await addressService.loadCities(selectedState);
          setCities(response);
        } catch (error) {
          console.error("Failed to load cities", error);
        }
      })();
    } else {
      setCities([]);
    }
  }, [selectedState, addressService]);

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }

    if (onChange) {
      onChange(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fields: GroupProp[] = [
    {
      groupName: "FILTER",
      items: [
        {
          name: "extendedLc",
          type: "select",
          label: "Serviço Nacional",
          placeholder: "Selecione o serviço",
          filter: true,
          showClear: true,
          options: nationalServices.map(ns => ({ 
            label: `${formatNationalTaxCode(ns.nationalTaxCode)} - ${ns.description}`, 
            value: ns.nationalTaxCode 
          })),
          onChange: (val: any) => {
            setFilters(prev => ({ ...prev, extendedLc: val.value || "", page: 0 }));
          },
          col: { xs: 12, md: 6, lg: 5 }
        },
        {
          name: "state",
          type: "select",
          label: "Estado",
          placeholder: "Estado",
          showClear: true,
          options: states.map(s => ({ label: s.name || s.initials || "", value: s.id })),
          onChange: (val: any) => {
            setSelectedState(val.value);
            setFilters(prev => ({ ...prev, cities: "", page: 0 }));
          },
          col: { xs: 6, md: 3, lg: 3 }
        },
        {
          name: "city",
          type: "select",
          filter: true,
          label: "Cidade",
          placeholder: "Cidade",
          showClear: true,
          options: cities.map(c => ({ label: c.name || "", value: String(c.ibge || "") })),
          onChange: (val: any) => {
            setFilters(prev => ({ ...prev, cities: val.value || "", page: 0 }));
          },
          col: { xs: 6, md: 3, lg: 4 }
        },
      ]
    }
  ];

  return (
    <S.ContainerFilter>
      <FormFactory
        groups={fields}
        onSubmit={() => { /* Filters are debounced */ }}
        button={{
          hide: true,
          submit: "Filtrar",
          submitting: "Filtrando..."
        }}
      />
    </S.ContainerFilter>
  );
};
